import React, { useCallback } from 'react'
import styled from 'styled-components'
import clsx from 'clsx'
import { rem } from 'polished'
import Clickable from 'components/Common/Clickable'
import { pluralizeToString } from 'lib/string/pluralize'
import { getLocalDate } from 'lib/datetime/calendarUtils'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import FormatCurrency from 'components/Common/FormatCurrency'
import TextLink from 'LuxKit/TextLink'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { displayPricingAs, getCalendarDisplayTotalPrice } from 'lib/hotels/bedbankUtils'
import SolidFireIcon from 'LuxKit/Icons/solid/SolidFireIcon'
import Group from 'components/utils/Group'

const CellWrapper = styled(Clickable)`
  display: flex;
  text-align: left;
  position: relative;
  overflow: hidden;
  align-items: stretch;
  background-color: ${props => props.theme.palette.neutral.default.eight};
  border-bottom: 1px solid ${props => props.theme.palette.neutral.default.five};
  transition: background-color 0.2s, color 0.2s;

  &:disabled, &.other-availability {
    background-color: ${props => props.theme.palette.neutral.default.seven};
  }
  &.check-in, &.check-out {
    box-shadow: none;
    background-color: ${props => props.theme.palette.highlight.primary.normalBackground};
  }

  &.between-day {
    background-color: ${props => props.theme.palette.highlight.primary.lightBackground}
  }
`

const SoldOutRecommendationsLink = styled(TextLink)`
  left: ${rem(4)};
  position: relative;
`

const DateText = styled.div`
  width: ${rem(72)};
  height: ${rem(60)};
  display: flex;
  justify-content: center;
  align-items: center;
  border-right: 1px solid ${props => props.theme.palette.neutral.default.five};
`

const DayOfWeek = styled.abbr`
  margin-right: ${rem(4)};
`

const Main = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex-grow: 1;
  height: 100%;
  padding: ${rem(8)};
  
  &.row-direction {
    flex-wrap: wrap;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
  }
`

const CheckInOutText = styled(BodyText)`
  position: absolute;
  right: ${rem(16)};
`

const RightBlock = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
`

const PlusFlightsText = styled(Caption)`
  margin-left: ${rem(4)};
`

interface Props {
  isCheckIn?: boolean
  isCheckOut?: boolean
  duration: number
  day: number
  hasPromo: boolean
  price?: number
  propertyFees?: number
  flightPrice?: number
  checkIn: string
  style?: React.CSSProperties
  className?: string
  isBetweenDay?: boolean
  fetching: boolean
  flights: boolean
  enableCalendarRecommendations: boolean
  adultsNumber: number
  roomsNumber: number
  displayPricingAsPerPerson: boolean
  onClick: () => void
}

function CalendarSidebarDayCell(props: Props) {
  const {
    duration,
    day,
    price,
    propertyFees,
    checkIn,
    isCheckIn,
    isCheckOut,
    style,
    className,
    isBetweenDay,
    hasPromo,
    fetching,
    flights,
    flightPrice,
    enableCalendarRecommendations,
    adultsNumber,
    roomsNumber,
    displayPricingAsPerPerson,
    onClick,
  } = props

  const dispatch = useOfferPageDispatch()
  const date = getLocalDate(checkIn)
  const greyDarkTextType = !isCheckIn ? 'neutral-three' : undefined
  const soldOut = hasPromo ? !price : false
  const showRecommendations = !fetching && soldOut && enableCalendarRecommendations
  const isNone = !isCheckIn && !isCheckOut && !isBetweenDay

  const onCellClick = useCallback(() => {
    if (isCheckIn) { return }
    if (showRecommendations) {
      dispatch({ type: OfferPageActions.showRecommendationsView, checkInDay: checkIn })
      return
    }
    onClick?.()
  }, [onClick, isCheckIn, dispatch, checkIn, showRecommendations])

  const hotelPrice = price ?? 0
  const hotelPropertyFees = propertyFees ?? 0

  const {
    total,
    saleUnit,
  } = getCalendarDisplayTotalPrice({
    price: hotelPrice + hotelPropertyFees,
    adultsNumber,
    roomsNumber,
    nights: duration,
    withFlights: !!flights,
    flightPrice: flightPrice ?? 0,
    displayPricingPer: displayPricingAs(displayPricingAsPerPerson),
  })

  return (
    <CellWrapper
      style={style}
      className={clsx(className, {
        'check-in': isCheckIn,
        'check-out': isCheckOut,
        'between-day': isBetweenDay,
      })}
      disabled={soldOut && !(showRecommendations && isNone)}
      onClick={onCellClick}
    >
      <DateText>
        <Heading as="span" variant="heading6">
          <DayOfWeek title={date.toLocaleDateString('en-GB', { weekday: 'long' })}>
            {date.toLocaleDateString('en-GB', { weekday: 'short' })}
          </DayOfWeek>
          <span>{day}</span>
        </Heading>
        <CheckInOutText variant="small" weight="bold">
          {isCheckIn && 'Check in'}
          {isCheckOut && 'Check out'}
        </CheckInOutText>
      </DateText>
      {!isCheckOut && !isBetweenDay &&
        <Main
          className={clsx({
            'row-direction': showRecommendations,
          })}
        >
          <Group direction="horizontal" verticalAlign="center" horizontalAlign="space-between">
            <Group direction="vertical">
              {!soldOut && hasPromo && <div>
                <Caption variant="medium" colour={greyDarkTextType}>
                  {pluralizeToString('night', duration)} {flights && flightPrice && '+ flights '} {' from'}
                </Caption>
                <BodyText as="span" variant="large" weight="bold" data-testid="day-price">
                  <BodyText colour={!isCheckIn ? 'highlight-secondary' : undefined} as="span" variant="large" weight="bold">
                    <FormatCurrency value={total} />
                    {displayPricingAsPerPerson && <Caption variant="medium" as="span">{saleUnit}</Caption>}
                  </BodyText>
                  {!displayPricingAsPerPerson && <Caption variant="medium" weight="normal" as="span"> {saleUnit}</Caption>}
                </BodyText>
                {flights && !flightPrice &&
                  <PlusFlightsText
                    variant="medium"
                    weight="bold"
                    forwardedAs="span"
                  >
                    + flights
                  </PlusFlightsText>}
              </div>}
              {!hasPromo && !isCheckIn && <Caption variant="medium" colour={greyDarkTextType} underline>Select to view prices</Caption>}
              {soldOut && !fetching && !enableCalendarRecommendations && <Caption variant="medium" colour={greyDarkTextType}>Check In Not Available</Caption>}
              {showRecommendations && (
                <>
                  <BodyText variant="small" colour="neutral-two">Sold out</BodyText>
                  <SoldOutRecommendationsLink size="small">View similar offers</SoldOutRecommendationsLink>
                </>
              )}
            </Group>
            {hasPromo && !isBetweenDay && !isCheckIn && !isCheckOut && <RightBlock><SolidFireIcon size="XS" colour="urgency" /></RightBlock>}
          </Group>
        </Main>}
    </CellWrapper>
  )
}

export default React.memo(CalendarSidebarDayCell)
